import { useMemo, useRef } from 'react';
import {
  buildOverviewForMonth,
  type OrgOverview,
  type OverviewMonthSelection,
} from '@/services/org-admin/overview';
import type { OrgUsageReport } from '@/services/org-admin';
import { useOrgMembers } from './useOrgMembers';
import { useOrgRoles } from './useOrgRoles';
import { useOrgUsageReport } from './useOrgUsageReport';
import { useOrgUsageServiceReport } from './useOrgUsageServiceReport';
import { useOrgUsageServiceReportByUsers } from './useOrgUsageServiceReportByUsers';
import { useOrgUsageTimeseries } from './useOrgUsageTimeseries';

/**
 * The Overview screen's single data dependency.
 *
 * Composes existing queries — every key here is one the People, Billing or Usage tab already uses,
 * so arriving on Overview warms the rest of the panel and returning to it is served from cache.
 *
 * DAYS MODE IS GONE. Overview used to offer 7/15/30-day windows alongside two calendar months, and
 * the funnel's three nested day windows were its best feature — one batched `useOrgUsageReports`
 * call produced a genuinely nested "active in 30d ⊇ 15d ⊇ 7d" funnel that no pair of calendar
 * months can reproduce. It was dropped anyway, because two range controls on one toolbar to explain
 * one screen is worse than losing the nesting, and because the Usage tab had already moved to
 * months — a reader comparing the two tabs was comparing different clocks. The nesting is partly
 * recovered by the quarter: `current` is a real subset of `this_quarter`, so selecting the quarter
 * gives back a funnel whose bars narrow monotonically (`ADOPTION_MONTHS`).
 *
 * WHICH REPORTS ARE FETCHED, AND WHY MORE THAN ONE. The screen needs the SELECTED period plus one
 * neighbour: the funnel plots two buckets, and the stickiness ratio needs a comparator. Those two
 * neighbours are not the same period — see `comparisonMonthFor` vs `stickinessComparatorFor` — so
 * the set below is the union of what both need, which works out to `current` always, `previous`
 * whenever a month is selected, and the quarter only when the quarter is selected. Fetching a
 * three-month aggregate to render a single-month view would be a request nothing on screen reads.
 *
 * `isFetching` is exposed separately from `isLoading` so changing the period dims the existing
 * charts rather than replacing them with skeletons: a chart that vanishes and returns reads as
 * broken.
 */
export interface UseOrgOverviewResult {
  data: OrgOverview | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useOrgOverview(
  month: OverviewMonthSelection,
  options?: { enabled?: boolean },
): UseOrgOverviewResult {
  const enabled = options?.enabled ?? true;
  const isQuarter = month === 'this_quarter';

  /*
   * Hooks cannot be called conditionally, so every query is mounted and `enabled` decides which
   * ones do network work — the same gating pattern this module already uses for non-admins.
   *
   * `current` is unconditional: it is either the selected period, the funnel's second bar, or the
   * stickiness comparator, and on the quarter it is all three.
   */
  const reportCurrent = useOrgUsageReport('current', { enabled });
  const reportPrevious = useOrgUsageReport('previous', { enabled: enabled && !isQuarter });
  const reportQuarter = useOrgUsageReport('this_quarter', { enabled: enabled && isQuarter });

  // Only the PRIMARY period's: these feed serviceEconomy/models/agentic/results/timeSaved, which
  // are about the selected period and never about its neighbour.
  const serviceReport = useOrgUsageServiceReport(month, { enabled });
  const timeseries = useOrgUsageTimeseries(month, { enabled });

  /*
   * Per-user reports for the funnel and the stickiness ratio. `current` again covers most cases; the
   * other two are gated to the selection that actually reads them. When `current` IS the selection
   * the first two calls share a query key and react-query dedupes them, so this is two requests
   * rather than three on the default view.
   */
  const serviceUsersCurrent = useOrgUsageServiceReportByUsers('current', { enabled });
  const serviceUsersPrevious = useOrgUsageServiceReportByUsers('previous', {
    enabled: enabled && !isQuarter,
  });
  const serviceUsersQuarter = useOrgUsageServiceReportByUsers('this_quarter', {
    enabled: enabled && isQuarter,
  });

  const roles = useOrgRoles();
  const members = useOrgMembers();

  const byMonth: Partial<Record<OverviewMonthSelection, OrgUsageReport>> = {
    current: reportCurrent.data,
    previous: reportPrevious.data,
    this_quarter: reportQuarter.data,
  };
  const serviceUsersByMonth = {
    current: serviceUsersCurrent.data,
    previous: serviceUsersPrevious.data,
    this_quarter: serviceUsersQuarter.data,
  };

  /*
   * "Now" is captured once per mount rather than read inside the adapter, which is pure.
   *
   * The per-seat limit projection derives from it, so reading the clock every render would shift it
   * mid-session and make the screen impossible to compare against a screenshot. Same reasoning, same
   * `useRef` shape, as `useUsageAnalysis`.
   */
  const nowRef = useRef<Date>();
  if (!nowRef.current) nowRef.current = new Date();

  const primaryReport = isQuarter ? reportQuarter : month === 'current' ? reportCurrent : reportPrevious;

  const data = useMemo(() => {
    // Nothing to build from yet. `null` lets the view show its loading state rather than a fully
    // zeroed overview, which would read as "your org did nothing".
    if (!byMonth[month] && !serviceReport.data) return null;
    return buildOverviewForMonth({
      month,
      reports: byMonth,
      serviceReport: serviceReport.data,
      serviceUsersByMonth,
      roles: roles.data,
      members: members.data,
      // A backend without the timeseries endpoint 404s here. That is not an error state for this
      // screen — the affected card explains itself — so the query's failure is deliberately not
      // folded into `isError` below.
      timeseries: timeseries.data,
      now: nowRef.current!,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `byMonth`/`serviceUsersByMonth` are
    // rebuilt every render by design; their underlying `.data` identities are the real dependencies
    // and are listed individually.
  }, [
    month,
    reportCurrent.data,
    reportPrevious.data,
    reportQuarter.data,
    serviceReport.data,
    serviceUsersCurrent.data,
    serviceUsersPrevious.data,
    serviceUsersQuarter.data,
    roles.data,
    members.data,
    timeseries.data,
  ]);

  return {
    data,
    isLoading: (primaryReport.isLoading || serviceReport.isLoading) && !data,
    // The neighbour period is excluded: the screen must not sit dimmed waiting on the funnel's
    // second bar when the figures above it have already arrived.
    isFetching: primaryReport.isFetching || serviceReport.isFetching,
    // Both primary sources are needed for a complete picture, but one failing should not blank the
    // screen — the adapter tolerates either being absent, so this is a hard error only when BOTH
    // fail. The timeseries query is excluded entirely; see the comment on `timeseries` above.
    isError: primaryReport.isError && serviceReport.isError,
    error: primaryReport.error ?? serviceReport.error,
    refetch: () => {
      void primaryReport.refetch();
      void serviceReport.refetch();
      void timeseries.refetch();
      void serviceUsersCurrent.refetch();
      if (isQuarter) {
        void serviceUsersQuarter.refetch();
        return;
      }
      void reportCurrent.refetch();
      void serviceUsersPrevious.refetch();
    },
  };
}
